import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { FiUpload, FiImage, FiFileText } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const NotesManager = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingNote, setEditingNote] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Prelims",
    file: null,
    thumbnail: null,
    isFree: false,
  });

  const [filterCategory, setFilterCategory] = useState("All");
  const [refresh, setRefresh] = useState(false);

  const token = JSON.parse(localStorage.getItem("user"))?.token;
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchNotes();
  }, [refresh, filterCategory]);

  // ================= FETCH =================
  const fetchNotes = async () => {
    setLoading(true);
    try {
      const url =
        filterCategory === "All"
          ? `${API_BASE}/api/admin/notes`
          : `${API_BASE}/api/admin/notes?category=${filterCategory}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setNotes(res.data);
    } catch {
      toast.error("Failed to fetch notes");
    } finally {
      setLoading(false);
    }
  };

  // ================= FORM =================
  const handleChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : files
          ? files[0]
          : value,
    }));
  };

// ================= UPLOAD / UPDATE =================
const handleUpload = async (e) => {

  e.preventDefault();

  try {

    // =========================
    // EDIT MODE
    // =========================

    if (editingNote) {

      const updateData = {

        title: formData.title,

        description:
          formData.description,

        category:
          formData.category,

        isFree:
          formData.isFree,

        publishDate:
          formData.publishDate,
      };

      await axios.put(

        `${API_BASE}/api/admin/notes/${editingNote._id}`,

        updateData,

        {
          headers: {

            Authorization:
              `Bearer ${token}`,
          },
        }
      );

      toast.success(
        "Note updated successfully 🚀"
      );

    }

    // =========================
    // CREATE MODE
    // =========================

    else {

      const uploadData =
        new FormData();

      Object.keys(formData).forEach(
        (key) => {

          if (
            formData[key] !== null &&
            formData[key] !== undefined
          ) {

            uploadData.append(
              key,
              formData[key]
            );
          }
        }
      );

      await axios.post(

        `${API_BASE}/api/admin/notes/upload`,

        uploadData,

        {
          headers: {

            Authorization:
              `Bearer ${token}`,

            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      toast.success(
        "Note uploaded 🚀"
      );
    }

    // =========================
    // RESET FORM
    // =========================

    setFormData({

      title: "",

      description: "",

      category: "Prelims",

      publishDate:
        new Date()
          .toISOString()
          .split("T")[0],

      file: null,

      thumbnail: null,

      isFree: false,
    });

    // =========================
    // EXIT EDIT MODE
    // =========================

    setEditingNote(null);

    // =========================
    // REFRESH NOTES
    // =========================

    setRefresh(!refresh);

  } catch (err) {

    console.error(err);

    toast.error(

      editingNote

        ? "Update failed"

        : "Upload failed"
    );
  }
};

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this note?")) return;

    try {
      await axios.delete(`${API_BASE}/api/admin/notes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Deleted");
      setRefresh(!refresh);
    } catch {
      toast.error("Delete failed");
    }
  };

  // ================= TOGGLE =================
  const toggleFreeStatus = async (id) => {
    try {
      await axios.put(
        `${API_BASE}/api/admin/notes/${id}/toggle-free`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setRefresh(!refresh);
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="p-6 space-y-10">
      <ToastContainer />

      {/* ================= UPLOAD CARD ================= */}
      <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 
        p-6 rounded-2xl shadow-xl border dark:border-gray-700">

        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
          Upload New Note
        </h2>

        <form onSubmit={handleUpload} className="space-y-5">

          <input
            type="text"
            name="title"
            placeholder="Enter note title"
            value={formData.title}
            onChange={handleChange}
            required
            className="w-full p-3 rounded-lg border dark:bg-gray-800 dark:text-white"
          />

          <div>
  <label className="block mb-2 font-medium dark:text-white">
    Description
  </label>

  <ReactQuill
    theme="snow"
    value={formData.description}
    onChange={(value) =>
      setFormData((prev) => ({
        ...prev,
        description: value,
      }))
    }
    className="bg-white dark:bg-gray-800 dark:text-white rounded-lg"
  />
</div>

          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full p-3 rounded-lg border dark:bg-gray-800 dark:text-white"
          >
            <option>Prelims</option>
            <option>Mains</option>
            <option>Prelims + Mains</option>
            <option>Extra</option>
          </select>

          <div>
  <label className="block mb-2 font-medium dark:text-white">
    Publish Date
  </label>

  <input
    type="date"
    name="publishDate"
    value={formData.publishDate}
    onChange={handleChange}
    className="w-full p-3 rounded-lg border dark:bg-gray-800 dark:text-white"
  />
</div>

          {/* FREE TOGGLE */}
          <label className="flex items-center gap-2 text-sm dark:text-gray-300">
            <input
              type="checkbox"
              name="isFree"
              checked={formData.isFree}
              onChange={handleChange}
            />
            Make this FREE
          </label>

          {/* PDF INPUT */}
          <div className="border p-3 rounded-lg dark:border-gray-600">
            <label className="text-sm font-semibold flex items-center gap-2 mb-2">
              <FiFileText /> Upload PDF
            </label>
            <input type="file" name="file" accept="application/pdf" onChange={handleChange} />
            {formData.file && (
              <p className="text-xs mt-1 text-gray-500">{formData.file.name}</p>
            )}
          </div>

          {/* THUMBNAIL INPUT */}
          <div className="border p-3 rounded-lg dark:border-gray-600">
            <label className="text-sm font-semibold flex items-center gap-2 mb-2">
              <FiImage /> Upload Thumbnail
            </label>
            <input type="file" name="thumbnail" accept="image/*" onChange={handleChange} />
            {formData.thumbnail && (
              <img
                src={URL.createObjectURL(formData.thumbnail)}
                alt="preview"
                className="mt-2 w-24 h-16 object-cover rounded"
              />
            )}
          </div>

          <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 
            text-white px-6 py-3 rounded-lg shadow">
            <FiUpload /> {editingNote
  ? "Update Note"
  : "Upload Note"}
          </button>
        </form>
      </div>

      {/* ================= TABLE ================= */}
      <div>
        <div className="flex justify-between mb-4">
          <h2 className="text-2xl font-bold dark:text-white">Uploaded Notes</h2>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="border px-3 py-1 rounded dark:bg-gray-800 dark:text-white"
          >
            <option value="All">All</option>
            <option value="Prelims">Prelims</option>
            <option value="Mains">Mains</option>
            <option value="Extra">Extra</option>
          </select>
        </div>

        {loading ? (
          <p className="text-center text-gray-400">Loading notes...</p>
        ) : notes.length === 0 ? (
          <p className="text-center text-gray-400">No notes uploaded yet</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {notes.map((note) => (
              <div
                key={note._id}
                className="bg-white dark:bg-gray-900 border dark:border-gray-700 
                rounded-xl shadow-md p-4"
              >
                {note.thumbnailUrl && (
                  <img
                    src={note.thumbnailUrl}
                    alt="thumb"
                    className="w-full h-32 object-cover rounded mb-3"
                  />
                )}

                <h3 className="font-semibold dark:text-white">{note.title}</h3>
                <p className="text-sm text-gray-500">{note.category}</p>

                <button
                  onClick={() => toggleFreeStatus(note._id)}
                  className={`mt-2 px-3 py-1 rounded text-sm ${
                    note.isFree
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  {note.isFree ? "Free" : "Premium"}
                </button>

                <div className="flex justify-between mt-3">
                  <a href={note.fileUrl} target="_blank" rel="noreferrer" className="text-blue-500">
                    View
                  </a>

                  <button
                    onClick={() => handleDelete(note._id)}
                    className="text-red-500"
                  >
                    Delete
                  </button>

                  <button onClick={() => { setEditingNote(note); setFormData({ title: note.title || "", description: note.description || "", category: note.category || "Prelims", isFree: note.isFree || false, publishDate: note.publishDate ? new Date( note.publishDate ) .toISOString() .split("T")[0] : "", }); window.scrollTo({ top: 0, behavior: "smooth", }); }} className=" bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm " > Edit </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesManager;